import React from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

/**
 * v2 Table system (TASK-104) — the one table language for dashboard lists,
 * the BOM modal, quote comparison and future lists. CSS grid rows (column
 * config = grid tracks), 10.5px/600 uppercase headers, 13px body rows, mono
 * columns for scannable data, 1px dividers, hover tint, empty/loading states,
 * sticky header inside a scroll container, and a footer slot for the
 * correction bar (see `CorrectionBar`). Sorting is controlled — the consumer
 * owns row order; the table renders affordances and calls `onSortChange`.
 */
export type TableAlign = 'left' | 'center' | 'right';
export type SortDirection = 'asc' | 'desc';
export interface TableSort {
  key: string;
  direction: SortDirection;
}

export interface TableColumn<T> {
  key: string;
  header?: React.ReactNode;
  /** CSS grid track, e.g. `'minmax(0,1fr)'` (default), `'120px'`. */
  width?: string;
  align?: TableAlign;
  /** IBM Plex Mono at 12.5px — IDs, prices, counts, dates. */
  mono?: boolean;
  sortable?: boolean;
  render?: (row: T, index: number) => React.ReactNode;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  /** Row padding: regular 11px · compact 7px. */
  density?: 'regular' | 'compact';
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  onRowClick?: (row: T, index: number) => void;
  selectedKeys?: string[];
  loading?: boolean;
  /** Skeleton rows while loading (default 5). */
  skeletonRows?: number;
  /** Shown when there are no rows and not loading. */
  empty?: React.ReactNode;
  /** Keep the header pinned — pair with `maxHeight` (or an outer scroll box). */
  stickyHeader?: boolean;
  /** Makes the row area a scroll container. */
  maxHeight?: number | string;
  /** 1px-border card frame (default). Turn off when a parent draws the frame. */
  framed?: boolean;
  /** Footer slot — e.g. `<CorrectionBar />` under AI-extracted data. */
  footer?: React.ReactNode;
  className?: string;
  'aria-label'?: string;
}

const alignClass: Record<TableAlign, string> = {
  left: 'text-left justify-start',
  center: 'text-center justify-center',
  right: 'text-right justify-end',
};

/** Loading bar fill — shimmer ramp from the v2 mocks (skeleton-only colors). */
const skeletonFill: React.CSSProperties = {
  background: 'linear-gradient(90deg,#EDEDF5 25%,#D9E4FB 40%,#E4DDFA 55%,#EDEDF5 70%)',
  backgroundSize: '200% 100%',
};
const SKELETON_WIDTHS = [92, 64, 78, 55];

function HeaderCell<T>({ col, sort, onSortChange }: {
  col: TableColumn<T>;
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
}) {
  const active = sort?.key === col.key ? sort.direction : undefined;
  if (!col.sortable || !onSortChange) {
    return (
      <span role="columnheader" className={`min-w-0 truncate ${alignClass[col.align ?? 'left']}`}>
        {col.header}
      </span>
    );
  }
  const Icon = active === 'asc' ? ChevronUp : active === 'desc' ? ChevronDown : ChevronsUpDown;
  return (
    <span
      role="columnheader"
      aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}
      className={`flex min-w-0 items-center ${alignClass[col.align ?? 'left']}`}
    >
      <button
        type="button"
        onClick={() => onSortChange({ key: col.key, direction: active === 'asc' ? 'desc' : 'asc' })}
        className={`inline-flex min-w-0 items-center gap-[3px] rounded-pill uppercase transition-colors
          focus:outline-none focus-visible:ring-2 focus-visible:ring-action
          ${active ? 'text-ink' : 'text-ink-muted hover:text-ink-secondary'}`}
      >
        <span className="truncate">{col.header}</span>
        <Icon className="h-[11px] w-[11px] shrink-0" />
      </button>
    </span>
  );
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  density = 'regular',
  sort,
  onSortChange,
  onRowClick,
  selectedKeys,
  loading = false,
  skeletonRows = 5,
  empty,
  stickyHeader = false,
  maxHeight,
  framed = true,
  footer,
  className = '',
  'aria-label': ariaLabel,
}: TableProps<T>): React.ReactElement {
  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: columns.map((c) => c.width ?? 'minmax(0,1fr)').join(' '),
    gap: '10px',
  };
  const rowPad = density === 'compact' ? 'py-[7px]' : 'py-[11px]';
  const selected = selectedKeys ? new Set(selectedKeys) : undefined;

  const cell = (col: TableColumn<T>, row: T, index: number) => (
    <span
      key={col.key}
      role="cell"
      className={`flex min-w-0 items-center ${alignClass[col.align ?? 'left']}
        ${col.mono ? 'font-mono text-mono-data text-ink' : 'text-body-md text-ink-body'}`}
    >
      {col.render ? col.render(row, index) : ((row as Record<string, unknown>)[col.key] as React.ReactNode)}
    </span>
  );

  return (
    <div
      role="table"
      aria-label={ariaLabel}
      className={`${framed ? 'overflow-hidden rounded-card border border-line' : ''} bg-surface ${className}`}
    >
      <div
        className="overflow-y-auto"
        style={maxHeight !== undefined ? { maxHeight } : undefined}
      >
        <div
          role="row"
          style={grid}
          className={`border-b border-line-divider-soft bg-surface px-s6 py-[8px]
            text-table-header uppercase text-ink-muted
            ${stickyHeader ? 'sticky top-0 z-[1]' : ''}`}
        >
          {columns.map((col) => (
            <HeaderCell key={col.key} col={col} sort={sort} onSortChange={onSortChange} />
          ))}
        </div>

        {loading &&
          Array.from({ length: skeletonRows }, (_, i) => (
            <div key={i} role="row" aria-hidden style={grid} className={`border-b border-line-divider px-s6 last:border-b-0 ${rowPad}`}>
              {columns.map((col, j) => (
                <span key={col.key} className="flex items-center">
                  <span
                    className="h-[9px] rounded-[5px] motion-safe:animate-shimmer"
                    style={{
                      ...skeletonFill,
                      width: `${SKELETON_WIDTHS[(i + j) % SKELETON_WIDTHS.length]}%`,
                      animationDelay: `${((i + j) % 4) * 0.15}s`,
                    }}
                  />
                </span>
              ))}
            </div>
          ))}

        {!loading && rows.length === 0 && (
          <div role="row" className="px-s6 py-s7 text-center text-body-sm text-ink-muted">
            {empty ?? 'Nothing here yet.'}
          </div>
        )}

        {!loading &&
          rows.map((row, index) => {
            const key = rowKey(row, index);
            const isSelected = selected?.has(key) ?? false;
            const clickable = Boolean(onRowClick);
            return (
              <div
                key={key}
                role="row"
                aria-selected={selected ? isSelected : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onRowClick?.(row, index) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick?.(row, index);
                        }
                      }
                    : undefined
                }
                style={grid}
                className={`border-b border-line-divider px-s6 last:border-b-0 ${rowPad}
                  ${isSelected ? 'bg-action-bg' : 'hover:bg-surface-hover'}
                  ${clickable ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action' : ''}`}
              >
                {columns.map((col) => cell(col, row, index))}
              </div>
            );
          })}
      </div>
      {footer}
    </div>
  );
}

export default Table;
